import React, {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { it } from '../layout/themeCloud/language/it';
import BluLoader from './Loader';

// 👇 lazy component che incapsula TUTTO Syncfusion
const SyncfusionPdfViewer = React.lazy(async () => {
  const [pdf, base] = await Promise.all([
    import('@syncfusion/ej2-react-pdfviewer'),
    import('@syncfusion/ej2-base'),
  ]);

  // carica la localizzazione italiana una sola volta
  base.L10n.load(it);

  // componente reale renderizzato dopo il lazy-load
  const LazyPdf: React.FC<{
    url: string;
    fileName: string;
  }> = ({ url, fileName }) => {
    const { PdfViewerComponent, Inject } = pdf;
    const viewerRef = useRef<InstanceType<typeof PdfViewerComponent>>(null);
    const [isPrinting, setIsPrinting] = useState(false);

    // Aumenta la qualità di stampa impostando printScaleRatio a 5
    // (API privata nella v24.2.9, ma effettivamente letta dal printModule)
    const onDocumentLoad = useCallback(() => {
      if (viewerRef.current) {
        (viewerRef.current as any).printScaleRatio = 5;
      }
    }, []);

    // Bypass della stampa Syncfusion su Chrome/Edge/Firefox.
    // Syncfusion usa canvas → JPEG 0.75 che degrada la qualità soprattutto su Edge/Firefox.
    // Usiamo saveAsBlob() per ottenere il PDF già in memoria (nessun re-fetch),
    // lo carichiamo in un iframe nascosto e chiamiamo print() — il browser stampa
    // il PDF con il suo renderer nativo a qualità piena.
    // Su Safari/iPad lasciamo che Syncfusion gestisca la stampa (lì funziona meglio).
    const onPrintStart = useCallback(
      (args: { cancel: boolean }) => {
        const isSafari =
          /^((?!chrome|android).)*safari/i.test(navigator.userAgent) ||
          /iPad|iPhone|iPod/.test(navigator.userAgent);
        // Su Safari/iPad non cancelliamo: Syncfusion gestisce la stampa
        if (isSafari) return;

        args.cancel = true;
        setIsPrinting(true);

        const viewer = viewerRef.current;
        // saveAsBlob() restituisce il PDF già caricato in memoria — nessun re-fetch
        const blobPromise = viewer
          ? viewer.saveAsBlob()
          : fetch(url).then((res) => res.blob());

        blobPromise
          .then((blob) => {
            const blobUrl = URL.createObjectURL(
              new Blob([blob], { type: 'application/pdf' }),
            );
            const iframe = document.createElement('iframe');
            iframe.style.position = 'fixed';
            iframe.style.top = '-10000px';
            iframe.style.left = '-10000px';
            iframe.style.width = '1px';
            iframe.style.height = '1px';
            iframe.src = blobUrl;

            iframe.onload = () => {
              try {
                iframe.contentWindow?.focus();
                iframe.contentWindow?.print();
              } catch {
                window.open(blobUrl, '_blank');
              }
              // Pulizia iframe e blob dopo tempo sufficiente
              setTimeout(() => {
                try {
                  document.body.removeChild(iframe);
                } catch {
                  /* iframe già rimosso */
                }
                URL.revokeObjectURL(blobUrl);
              }, 60000);
            };

            iframe.onerror = () => {
              try {
                document.body.removeChild(iframe);
              } catch {
                /* già rimosso */
              }
              URL.revokeObjectURL(blobUrl);
            };

            document.body.appendChild(iframe);
          })
          .catch(() => {
            // Fallback: apri l'URL in nuova tab
            window.open(url, '_blank');
          })
          .finally(() => {
            setIsPrinting(false);
          });
      },
      [url],
    );

    return (
      <>
        <BluLoader open={isPrinting} />
        <PdfViewerComponent
          ref={viewerRef}
          id="container"
          locale="it"
          documentPath={url}
          downloadFileName={fileName}
          documentLoad={onDocumentLoad}
          printStart={onPrintStart}
          printMode="NewWindow"
          toolbarSettings={{
            showTooltip: true,
            toolbarItems: [
              'UndoRedoTool',
              'PageNavigationTool',
              'MagnificationTool',
              'PrintOption',
              'DownloadOption',
            ],
          }}
          // ⚠️ Mantenere allineata con la versione di @syncfusion/ej2-react-pdfviewer in package.json
          resourceUrl="https://cdn.syncfusion.com/ej2/24.2.9/dist/ej2-pdfviewer-lib"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        >
          <Inject
            services={[
              pdf.Toolbar,
              pdf.Magnification,
              pdf.Navigation,
              pdf.Print,
            ]}
          />
        </PdfViewerComponent>
      </>
    );
  };

  return { default: LazyPdf };
});

interface Props {
  show: boolean;
  url: string;
  title?: string;
  handleClose: () => void;
  /** Show WhatsApp send button */
  showWhatsAppButton?: boolean;
  /** Callback when WhatsApp button is clicked */
  onSendWhatsApp?: () => void;
}

/**
 * Visualizza un dialogo contenente un visualizzatore PDF (lazy-loaded).
 */
function DialogPdfViewer(props: Props) {
  const { show, title, url, showWhatsAppButton, onSendWhatsApp } = props;

  const path = url.split('?')[0];
  let fileName = path.substring(path.lastIndexOf('/') + 1);
  if (!fileName.includes('.pdf')) fileName = null as unknown as string;
  if (fileName === 'ricerca.pdf') fileName = `${title}.pdf`;

  useEffect(() => {
    window.dispatchEvent(new Event('openingPdfViewer'));

    return () => {
      window.dispatchEvent(new Event('closingPdfViewer'));
    };
  }, []);

  return (
    <Dialog open={show} fullWidth maxWidth="xl" onClose={props.handleClose}>
      <DialogTitle>{title || 'Documento'}</DialogTitle>

      <DialogContent
        sx={{
          p: 1,
          display: 'flex',
          height: 800,
          position: 'relative',
        }}
      >
        <Suspense fallback={<BluLoader open />}>
          <SyncfusionPdfViewer
            url={url}
            fileName={fileName || 'documento.pdf'}
          />
        </Suspense>
      </DialogContent>

      <DialogActions>
        {showWhatsAppButton && onSendWhatsApp && (
          <Button
            variant="contained"
            color="success"
            startIcon={<WhatsAppIcon />}
            onClick={onSendWhatsApp}
          >
            Invia WhatsApp
          </Button>
        )}
        <Button variant="outlined" onClick={props.handleClose}>
          Chiudi
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DialogPdfViewer;
